import Phaser from "phaser";
import {
  ModalLayer,
  NINE_SLICE_BUTTON_EVENT,
  NineSliceButton,
  NineSlicePanel,
  ScrollableList,
  type ButtonStyleToken,
  type PanelStyleToken,
} from "@yjsgames/phaser-ui";
import { CheatTextInput } from "./CheatTextInput.js";
import { computeDropdownListY, computePopoverPosition, layoutCommandButtons } from "./layout.js";
import {
  groupCheatCommands,
  initialExpandedState,
  parseRecentCheatHistory,
  pushRecentCheatCommand,
  RECENT_CHEAT_GROUP_ID,
  resolveControlDigitShortcut,
  resolveRecentShortcutCommand,
  resolveCheatSuccessMessage,
  withRecentCheatGroup,
  type RecentCheatHistory,
} from "./model.js";
import type {
  CheatCommand,
  CheatPanelSpec,
  CheatParam,
  CheatParamValue,
  CheatParamValues,
} from "./types.js";

export interface CheatPanelTheme {
  readonly overlayColor: number;
  readonly overlayAlpha?: number;
  readonly titleColor: string;
  readonly textColor: string;
  readonly dimTextColor: string;
  readonly errorColor: string;
}

export interface CheatCommandFeedbackEvent {
  readonly command: CheatCommand;
  readonly values: CheatParamValues;
  readonly message?: string;
}

export interface CheatCommandErrorEvent {
  readonly command: CheatCommand;
  readonly values: CheatParamValues;
  readonly error: unknown;
}

export interface CheatPanelFeedback {
  readonly success?: (event: CheatCommandFeedbackEvent) => void;
  readonly error?: (event: CheatCommandErrorEvent) => void;
}

export interface CheatPanelOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  readonly spec: CheatPanelSpec;
  readonly panelStyle: PanelStyleToken;
  readonly buttonStyle: ButtonStyleToken;
  readonly fontFamily: string;
  readonly fontSize: {
    readonly title: string;
    readonly body: string;
    readonly caption: string;
  };
  readonly theme: CheatPanelTheme;
  readonly onClose: () => void;
  readonly feedback?: CheatPanelFeedback;
  readonly panelWidth?: number;
  readonly panelMaxHeight?: number;
  readonly recentStorageKey?: string;
  readonly initialControlHeld?: boolean;
}

interface ParamControl {
  readonly param: CheatParam;
  readonly value: () => CheatParamValue | null;
}

interface RecentShortcutButton {
  readonly command: CheatCommand;
  readonly button: NineSliceButton;
  readonly shortcutIndex: number;
}

interface DropdownShortcutButton {
  readonly option: string;
  readonly button: NineSliceButton;
  readonly shortcutIndex: number;
  readonly select: () => void;
}

const HEADER_H = 54;
const BODY_PAD = 12;
const GROUP_H = 30;
const GROUP_GAP = 8;
const BUTTON_GAP_X = 8;
const BUTTON_GAP_Y = 8;
const DEFAULT_BUTTON_W = 156;
const DEFAULT_BUTTON_H = 36;
const POPOVER_W = 260;
const PARAM_ROW_H = 42;
const CONTROL_X = 180;
const CONTROL_W = 128;
const CONTROL_H = 30;
const SELECT_OPTION_H = 28;
const DEFAULT_RECENT_STORAGE_KEY = "@yjsgames/phaser-cheat/recent";

export class CheatPanel extends ModalLayer {
  private readonly baseSpec: CheatPanelSpec;
  private spec: CheatPanelSpec;
  private readonly panelStyle: PanelStyleToken;
  private readonly buttonStyle: ButtonStyleToken;
  private readonly fontFamily: string;
  private readonly fontSize: CheatPanelOptions["fontSize"];
  private readonly theme: CheatPanelTheme;
  private readonly feedback?: CheatPanelFeedback;
  private readonly recentStorageKey: string;
  private readonly onClose: () => void;
  private recentHistory: RecentCheatHistory;
  private readonly scrollBody: ScrollableList;
  private readonly expanded: Record<string, boolean>;
  private readonly recentShortcutButtons: RecentShortcutButton[] = [];
  private readonly dropdownShortcutButtons: DropdownShortcutButton[] = [];
  private readonly popoverSelectOpeners: Array<() => boolean> = [];
  private controlHeld = false;
  private popover?: Phaser.GameObjects.Container;
  private dropdown?: Phaser.GameObjects.Container;
  private popoverSubmit?: () => void;
  private popoverCancel?: () => void;

  private readonly onShortcutKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Control" && !this.controlHeld) {
      this.controlHeld = true;
      this.updateRecentShortcutLabels();
      this.updateDropdownShortcutLabels();
      return;
    }
    if (this.popover && event.key === "Escape") {
      event.preventDefault();
      this.popoverCancel?.();
      return;
    }
    if (!this.popover && event.key === "Escape") {
      event.preventDefault();
      this.onClose();
      return;
    }
    if (this.popover && event.key === "Enter") {
      event.preventDefault();
      this.popoverSubmit?.();
      return;
    }
    if (this.popover && event.key === "Tab") {
      event.preventDefault();
      this.openFirstPopoverDropdown();
      return;
    }
    const shortcutIndex = resolveControlDigitShortcut(event);
    if (shortcutIndex === null) return;
    event.preventDefault();
    if (event.repeat) return;
    if (this.selectDropdownShortcut(shortcutIndex)) return;
    if (this.popover) return;
    const command = resolveRecentShortcutCommand(this.spec, shortcutIndex);
    const entry = this.recentShortcutButtons.find((button) => button.command.id === command?.id);
    if (!command || !entry) return;
    this.activateCommandButton(command, entry.button);
  };

  private readonly onShortcutKeyUp = (event: KeyboardEvent): void => {
    if (event.key !== "Control") return;
    this.controlHeld = false;
    this.updateRecentShortcutLabels();
    this.updateDropdownShortcutLabels();
  };

  private readonly onShortcutBlur = (): void => {
    if (!this.controlHeld) return;
    this.controlHeld = false;
    this.updateRecentShortcutLabels();
    this.updateDropdownShortcutLabels();
  };

  constructor(scene: Phaser.Scene, opts: CheatPanelOptions) {
    super(scene, {
      screenWidth: opts.screenWidth,
      screenHeight: opts.screenHeight,
      overlayColor: opts.theme.overlayColor,
      overlayAlpha: opts.theme.overlayAlpha ?? 0.82,
      onBackdropClick: opts.onClose,
    });

    this.baseSpec = opts.spec;
    this.recentStorageKey = opts.recentStorageKey ?? DEFAULT_RECENT_STORAGE_KEY;
    this.recentHistory = this.loadRecentHistory(Date.now());
    this.spec = withRecentCheatGroup(this.baseSpec, this.recentHistory);
    this.panelStyle = opts.panelStyle;
    this.buttonStyle = opts.buttonStyle;
    this.fontFamily = opts.fontFamily;
    this.fontSize = opts.fontSize;
    this.theme = opts.theme;
    this.feedback = opts.feedback;
    this.onClose = opts.onClose;
    this.controlHeld = opts.initialControlHeld ?? false;
    this.expanded = initialExpandedState(this.spec);

    const panelW = Math.min(opts.panelWidth ?? 366, opts.screenWidth - 16);
    const panelH = Math.min(opts.panelMaxHeight ?? 720, opts.screenHeight - 16);
    const panelX = (opts.screenWidth - panelW) / 2;
    const panelY = (opts.screenHeight - panelH) / 2;

    const panel = new NineSlicePanel(scene, panelX, panelY, {
      style: opts.panelStyle,
      width: panelW,
      height: panelH,
      originX: 0,
      originY: 0,
    });
    this.contentContainer.add(panel);
    this.setBackdropCloseExclusion(panelX, panelY, panelW, panelH);

    this.contentContainer.add(
      scene.add
        .text(panelX + panelW / 2, panelY + 24, opts.spec.title, {
          fontFamily: opts.fontFamily,
          fontSize: opts.fontSize.title,
          color: opts.theme.titleColor,
        })
        .setOrigin(0.5),
    );

    const close = scene.add
      .text(panelX + panelW - 18, panelY + 20, "X", {
        fontFamily: opts.fontFamily,
        fontSize: opts.fontSize.title,
        color: opts.theme.dimTextColor,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    close.on("pointerup", opts.onClose);
    this.contentContainer.add(close);

    this.scrollBody = new ScrollableList(scene, panelX + BODY_PAD, panelY + HEADER_H, {
      width: panelW - BODY_PAD * 2,
      height: panelH - HEADER_H - BODY_PAD,
      barWidth: 5,
      itemMargin: 52,
    });
    this.contentContainer.add(this.scrollBody);
    this.installShortcutListeners();
    this.renderBody();
  }

  private renderBody(): void {
    this.closePopover();
    this.scrollBody.clearContent();
    this.recentShortcutButtons.length = 0;
    let y = 0;
    const bodyW = this.scrollBody.viewWidth;
    const groups = groupCheatCommands(this.spec);

    for (const { group, commands } of groups) {
      const header = this.scene.add
        .text(0, 0, `${this.expanded[group.id] ? "v" : ">"} ${group.label}`, {
          fontFamily: this.fontFamily,
          fontSize: this.fontSize.body,
          color: this.theme.textColor,
        })
        .setOrigin(0, 0.5)
        .setInteractive({ useHandCursor: true });
      header.on("pointerup", () => {
        this.expanded[group.id] = !this.expanded[group.id];
        this.renderBody();
      });
      this.scrollBody.addContent(header, 0, y + GROUP_H / 2);
      y += GROUP_H;

      if (this.expanded[group.id]) {
        const layout = layoutCommandButtons(
          commands.map((command) => ({
            id: command.id,
            width: command.buttonWidth,
            height: command.buttonHeight,
          })),
          {
            contentWidth: bodyW,
            defaultWidth: DEFAULT_BUTTON_W,
            defaultHeight: DEFAULT_BUTTON_H,
            gapX: BUTTON_GAP_X,
            gapY: BUTTON_GAP_Y,
            startY: y,
          },
        );
        for (const command of commands) {
          const placement = layout.placements.find((it) => it.id === command.id);
          if (!placement) continue;
          const button = this.addCommandButton(
            command,
            placement.x + placement.width / 2,
            placement.y + placement.height / 2,
          );
          if (group.id === RECENT_CHEAT_GROUP_ID && this.recentShortcutButtons.length < 9) {
            this.recentShortcutButtons.push({
              command,
              button,
              shortcutIndex: this.recentShortcutButtons.length,
            });
          }
        }
        y += layout.totalHeight + GROUP_GAP;
      } else {
        y += GROUP_GAP;
      }
    }
    this.scrollBody.setContentHeight(y + GROUP_GAP);
    this.updateRecentShortcutLabels();
  }

  private addCommandButton(command: CheatCommand, x: number, y: number): NineSliceButton {
    const button = new NineSliceButton(this.scene, 0, 0, {
      style: this.buttonStyle,
      label: command.label,
      widthOverride: command.buttonWidth ?? DEFAULT_BUTTON_W,
      heightOverride: command.buttonHeight ?? DEFAULT_BUTTON_H,
    });
    button.on(NINE_SLICE_BUTTON_EVENT.Click, () => {
      this.activateCommandButton(command, button);
    });
    this.scrollBody.addContent(button, x, y);
    return button;
  }

  private activateCommandButton(command: CheatCommand, button: NineSliceButton): void {
    if ((command.params?.length ?? 0) === 0) {
      this.runCommand(command, {});
      return;
    }
    this.openPopover(command, button);
  }

  private openPopover(command: CheatCommand, anchorObject: Phaser.GameObjects.Container): void {
    this.closePopover();
    this.popoverSelectOpeners.length = 0;
    const params = command.params ?? [];
    const popoverH = 92 + params.length * PARAM_ROW_H;
    const matrix = anchorObject.getWorldTransformMatrix();
    const anchorW = command.buttonWidth ?? DEFAULT_BUTTON_W;
    const anchorH = command.buttonHeight ?? DEFAULT_BUTTON_H;
    const pos = computePopoverPosition({
      anchor: { x: matrix.tx - anchorW / 2, y: matrix.ty - anchorH / 2, width: anchorW, height: anchorH },
      popover: { width: POPOVER_W, height: popoverH },
      viewport: { width: this.screenWidth, height: this.screenHeight },
      margin: 8,
    });

    const popover = this.scene.add.container(pos.x, pos.y);
    const bg = new NineSlicePanel(this.scene, 0, 0, {
      style: this.panelStyle,
      width: POPOVER_W,
      height: popoverH,
      originX: 0,
      originY: 0,
    });
    popover.add(bg);
    popover.add(
      this.scene.add
        .text(12, 18, command.label, {
          fontFamily: this.fontFamily,
          fontSize: this.fontSize.body,
          color: this.theme.textColor,
        })
        .setOrigin(0, 0.5),
    );

    const close = this.scene.add
      .text(POPOVER_W - 16, 18, "X", {
        fontFamily: this.fontFamily,
        fontSize: this.fontSize.body,
        color: this.theme.dimTextColor,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    close.on("pointerup", () => this.closePopover());
    popover.add(close);

    const error = this.scene.add
      .text(12, popoverH - 48, "", {
        fontFamily: this.fontFamily,
        fontSize: this.fontSize.caption,
        color: this.theme.errorColor,
      })
      .setOrigin(0, 0.5);
    popover.add(error);

    const controls: ParamControl[] = [];
    params.forEach((param, index) => {
      controls.push(this.addParamControl(popover, param, 44 + index * PARAM_ROW_H));
    });

    const cancel = new NineSliceButton(this.scene, POPOVER_W - 124, popoverH - 22, {
      style: this.buttonStyle,
      label: "취소",
      widthOverride: 72,
      heightOverride: 30,
    });
    cancel.on(NINE_SLICE_BUTTON_EVENT.Click, () => this.closePopover());
    popover.add(cancel);

    const submitCurrent = (): void => {
      const values: Record<string, CheatParamValue> = {};
      for (const control of controls) {
        const value = control.value();
        if (value === null) {
          error.setText(`${control.param.label} 입력 오류`);
          return;
        }
        values[control.param.id] = value;
      }
      this.runCommand(command, values);
      this.closePopover();
    };

    const submit = new NineSliceButton(this.scene, POPOVER_W - 46, popoverH - 22, {
      style: this.buttonStyle,
      label: "실행",
      widthOverride: 72,
      heightOverride: 30,
    });
    submit.on(NINE_SLICE_BUTTON_EVENT.Click, submitCurrent);
    popover.add(submit);
    this.contentContainer.add(popover);
    this.popover = popover;
    this.popoverSubmit = submitCurrent;
    this.popoverCancel = () => this.closePopover();
  }

  private addParamControl(
    popover: Phaser.GameObjects.Container,
    param: CheatParam,
    y: number,
  ): ParamControl {
    popover.add(
      this.scene.add
        .text(12, y, param.label, {
          fontFamily: this.fontFamily,
          fontSize: this.fontSize.caption,
          color: this.theme.dimTextColor,
        })
        .setOrigin(0, 0.5),
    );

    if (param.type === "boolean") {
      let value = param.defaultValue ?? false;
      const btn = new NineSliceButton(this.scene, CONTROL_X, y, {
        style: this.buttonStyle,
        label: value ? "ON" : "OFF",
        widthOverride: 92,
        heightOverride: CONTROL_H,
      });
      btn.on(NINE_SLICE_BUTTON_EVENT.Click, () => {
        value = !value;
        btn.setLabel(value ? "ON" : "OFF");
      });
      popover.add(btn);
      return { param, value: () => value };
    }

    if (param.type === "select") {
      let index = Math.max(0, param.options.indexOf(param.defaultValue ?? param.options[0] ?? ""));
      const current = (): string => param.options[index] ?? "";
      const label = () => this.formatSelectLabel(current());
      const btn = new NineSliceButton(this.scene, CONTROL_X, y, {
        style: this.buttonStyle,
        label: label(),
        widthOverride: CONTROL_W,
        heightOverride: CONTROL_H,
      });
      const selectOption = (optionIndex: number): void => {
        index = optionIndex;
        btn.setLabel(label());
        this.closeDropdown();
      };
      const openDropdown = (): boolean => {
        if (param.options.length === 0) return false;
        if (this.dropdown) return true;

        const dropdownH = param.options.length * SELECT_OPTION_H + 8;
        const dropdown = this.scene.add.container(
          CONTROL_X - CONTROL_W / 2,
          computeDropdownListY({
            controlY: y,
            controlHeight: CONTROL_H,
            dropdownHeight: dropdownH,
            popoverY: popover.y,
            viewportHeight: this.screenHeight,
          }),
        );
        dropdown.add(
          new NineSlicePanel(this.scene, 0, 0, {
            style: this.panelStyle,
            width: CONTROL_W,
            height: dropdownH,
            originX: 0,
            originY: 0,
          }),
        );
        this.dropdownShortcutButtons.length = 0;
        param.options.forEach((option, optionIndex) => {
          const optionButton = new NineSliceButton(
            this.scene,
            CONTROL_W / 2,
            4 + optionIndex * SELECT_OPTION_H + SELECT_OPTION_H / 2,
            {
              style: this.buttonStyle,
              label: this.formatSelectLabel(option),
              widthOverride: CONTROL_W - 8,
              heightOverride: SELECT_OPTION_H - 2,
            },
          );
          optionButton.on(NINE_SLICE_BUTTON_EVENT.Click, () => {
            selectOption(optionIndex);
          });
          dropdown.add(optionButton);
          if (this.dropdownShortcutButtons.length < 9) {
            this.dropdownShortcutButtons.push({
              option,
              button: optionButton,
              shortcutIndex: this.dropdownShortcutButtons.length,
              select: () => selectOption(optionIndex),
            });
          }
        });
        popover.add(dropdown);
        this.dropdown = dropdown;
        this.updateDropdownShortcutLabels();
        return true;
      };
      this.popoverSelectOpeners.push(openDropdown);
      btn.on(NINE_SLICE_BUTTON_EVENT.Click, () => {
        if (this.dropdown) {
          this.closeDropdown();
          return;
        }
        openDropdown();
      });
      popover.add(btn);
      return { param, value: () => current() };
    }

    const input = new CheatTextInput(this.scene, CONTROL_X, y, {
      width: CONTROL_W,
      height: CONTROL_H,
      panelStyle: this.panelStyle,
      fontFamily: this.fontFamily,
      fontSize: this.fontSize.caption,
      textColor: this.theme.textColor,
      placeholderColor: this.theme.dimTextColor,
      placeholder: param.placeholder,
    });
    input.setValue(String(param.defaultValue ?? ""));
    popover.add(input);
    return {
      param,
      value: () => {
        const raw = input.getValue();
        if (param.parse) return param.parse(raw);
        if (param.type === "number") {
          const n = Number(raw.trim());
          return Number.isFinite(n) ? n : null;
        }
        return raw;
      },
    };
  }

  private closePopover(): void {
    this.closeDropdown();
    this.popover?.destroy();
    this.popover = undefined;
    this.popoverSubmit = undefined;
    this.popoverCancel = undefined;
    this.popoverSelectOpeners.length = 0;
  }

  private closeDropdown(): void {
    this.dropdown?.destroy();
    this.dropdown = undefined;
    this.dropdownShortcutButtons.length = 0;
  }

  private formatSelectLabel(value: string): string {
    if (value.length <= 16) return `${value} v`;
    return `${value.slice(0, 14)}... v`;
  }

  private runCommand(command: CheatCommand, values: CheatParamValues): void {
    try {
      command.run(values);
    } catch (error) {
      this.feedback?.error?.({ command, values, error });
      throw error;
    }

    this.recordRecentCommand(command.id);
    const message = resolveCheatSuccessMessage(command, values);
    if (message === false) return;
    this.feedback?.success?.({ command, values, message });
  }

  private recordRecentCommand(commandId: string): void {
    this.recentHistory = pushRecentCheatCommand(this.recentHistory, commandId, Date.now());
    this.persistRecentHistory();
    this.spec = withRecentCheatGroup(this.baseSpec, this.recentHistory);
    for (const [id, expanded] of Object.entries(initialExpandedState(this.spec))) {
      if (this.expanded[id] === undefined) this.expanded[id] = expanded;
    }
    this.renderBody();
  }

  private loadRecentHistory(nowMs: number): RecentCheatHistory {
    if (typeof window === "undefined") return parseRecentCheatHistory(null, nowMs);
    try {
      return parseRecentCheatHistory(window.localStorage.getItem(this.recentStorageKey), nowMs);
    } catch {
      return parseRecentCheatHistory(null, nowMs);
    }
  }

  private persistRecentHistory(): void {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(this.recentStorageKey, JSON.stringify(this.recentHistory));
    } catch {
      // Dev convenience only. Storage quota/private mode errors must not break cheats.
    }
  }

  private updateRecentShortcutLabels(): void {
    for (const { command, button, shortcutIndex } of this.recentShortcutButtons) {
      button.setLabel(this.controlHeld ? `${command.label} (${shortcutIndex + 1})` : command.label);
    }
  }

  private openFirstPopoverDropdown(): void {
    for (const open of this.popoverSelectOpeners) {
      if (open()) return;
    }
  }

  private selectDropdownShortcut(shortcutIndex: number): boolean {
    if (!this.controlHeld || !this.dropdown) return false;
    const entry = this.dropdownShortcutButtons[shortcutIndex];
    if (!entry) return false;
    entry.select();
    return true;
  }

  private updateDropdownShortcutLabels(): void {
    for (const { option, button, shortcutIndex } of this.dropdownShortcutButtons) {
      const base = this.formatSelectLabel(option);
      button.setLabel(this.controlHeld ? `${base} (${shortcutIndex + 1})` : base);
    }
  }

  private installShortcutListeners(): void {
    if (typeof window === "undefined") return;
    window.addEventListener("keydown", this.onShortcutKeyDown);
    window.addEventListener("keyup", this.onShortcutKeyUp);
    window.addEventListener("blur", this.onShortcutBlur);
  }

  private removeShortcutListeners(): void {
    if (typeof window === "undefined") return;
    window.removeEventListener("keydown", this.onShortcutKeyDown);
    window.removeEventListener("keyup", this.onShortcutKeyUp);
    window.removeEventListener("blur", this.onShortcutBlur);
  }

  override destroy(fromScene?: boolean): void {
    this.removeShortcutListeners();
    super.destroy(fromScene);
  }
}
